import axios from "axios";
import { useEffect, useMemo, useRef, useState } from "react";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { toast } from "sonner";
import { Camera, CreditCard, ExternalLink, FileUp, Loader2, Lock, MessageCircle, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PTOK = () => `Bearer ${localStorage.getItem("fr_partner_token")}`;

const formatDetail = (d) => {
  if (d == null) return "Errore di rete, riprova.";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  if (typeof d?.msg === "string") return d.msg;
  return String(d);
};

const PSTAT_BADGE = {
  in_attesa: "bg-amber-100 text-amber-800", autorizzato: "bg-blue-100 text-blue-800",
  incassato: "bg-green-100 text-green-800", cauzione_rilasciata: "bg-gray-100 text-gray-600",
  annullato: "bg-gray-100 text-gray-600", rimborsato: "bg-gray-100 text-gray-600", fallito: "bg-red-100 text-fr-red",
};

/* Upload documenti ospite (patente + identità): foto o file — Fase 4.2 */
export const PartnerDocUpload = ({ docs, setDocs }) => {
  const camRef = useRef(null);
  const fileRef = useRef(null);
  const [label, setLabel] = useState("patente");
  const [busy, setBusy] = useState(false);

  const up = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/partner/upload`, fd, { headers: { Authorization: PTOK(), "Content-Type": "multipart/form-data" } });
      setDocs((d) => [...d, { step: label, path: data.path, filename: data.filename }]);
      toast.success(`Documento ${label} caricato`);
    } catch (err) {
      toast.error(formatDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
      if (camRef.current) camRef.current.value = "";
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div className="sm:col-span-3 rounded-xl border border-gray-100 bg-white p-2.5" data-testid="partner-docs-upload">
      <p className="text-[10px] font-bold text-gray-400 uppercase">Documenti dell'ospite (il driver lascia solo le chiavi: caricare PRIMA della consegna)</p>
      <div className="mt-1.5 flex flex-wrap items-center gap-2">
        <select value={label} onChange={(e) => setLabel(e.target.value)} className="h-8 rounded-lg border border-gray-200 bg-gray-50 px-2 text-xs" data-testid="partner-doc-type">
          <option value="patente">Patente di guida</option>
          <option value="identita">Documento d'identità</option>
        </select>
        <input ref={camRef} type="file" accept="image/*" capture="environment" onChange={up} className="hidden" data-testid="partner-doc-camera-input" />
        <input ref={fileRef} type="file" accept="image/*,.pdf" onChange={up} className="hidden" data-testid="partner-doc-file-input" />
        <button type="button" onClick={() => camRef.current?.click()} className="h-8 rounded-lg bg-fr-navy text-white text-[10px] font-bold px-2.5 flex items-center gap-1" data-testid="partner-doc-camera">
          {busy ? <Loader2 size={11} className="animate-spin" /> : <Camera size={11} />} Scatta una foto
        </button>
        <button type="button" onClick={() => fileRef.current?.click()} className="h-8 rounded-lg bg-gray-100 text-gray-700 text-[10px] font-bold px-2.5 flex items-center gap-1" data-testid="partner-doc-file">
          <FileUp size={11} /> Allega file
        </button>
        {docs.length > 0 && <span className="text-[10px] font-bold text-green-700" data-testid="partner-docs-count">{docs.length} caricati ✓</span>}
      </div>
    </div>
  );
};

/* ---------- Stripe: form interno al provider Elements ---------- */
const PayForm = ({ clientSecret, booking, onClose }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);

  const pay = async () => {
    if (!stripe || !elements) return;
    setProcessing(true);
    try {
      const { error, paymentIntent } = await stripe.confirmPayment({ elements, clientSecret, redirect: "if_required" });
      if (error) toast.error(error.message || "Pagamento non riuscito");
      else toast.success(`Pagamento ${paymentIntent?.status === "succeeded" ? "completato" : "autorizzato"}: il gestionale incasserà a fine noleggio`);
      onClose(true);
    } catch {
      toast.error("Pagamento non riuscito");
    } finally {
      setProcessing(false);
    }
  };

  return (
    <>
      <PaymentElement />
      <button onClick={pay} disabled={processing} className="mt-3 w-full h-11 rounded-xl bg-fr-navy text-white font-bold text-sm flex items-center justify-center gap-2 disabled:opacity-60" data-testid="stripe-modal-pay">
        {processing ? <Loader2 size={15} className="animate-spin" /> : <Lock size={14} />} Autorizza (preautorizzazione, non un incasso)
      </button>
    </>
  );
};

export const StripeModal = ({ publishableKey, clientSecret, booking, onClose }) => {
  const stripePromise = useMemo(() => loadStripe(publishableKey), [publishableKey]);
  if (!stripePromise) return null;
  return (
    <div className="fixed inset-0 z-[70] bg-black/60 flex items-center justify-center p-4" onClick={() => onClose(false)} data-testid="stripe-modal">
      <div className="bg-white rounded-2xl w-full max-w-sm p-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <p className="font-extrabold text-fr-navy text-sm">Autorizza {booking.reference}</p>
          <button onClick={() => onClose(false)} data-testid="stripe-modal-close"><X size={16} className="text-gray-400" /></button>
        </div>
        <p className="text-[10px] text-gray-400 mt-1">Modalità TEST — nessun denaro reale. Nessun dato carta tocca i server FabriRent.</p>
        <Elements stripe={stripePromise} options={{ clientSecret, appearance: { theme: "stripe" } }}>
          <PayForm clientSecret={clientSecret} booking={booking} onClose={onClose} />
        </Elements>
      </div>
    </div>
  );
};

/* ---------- Pannello pagamenti di una prenotazione partner ---------- */
export const PartnerPaymentPanel = ({ booking }) => {
  const [payment, setPayment] = useState(undefined);
  const [stripeModal, setStripeModal] = useState(null);
  const [link, setLink] = useState(null);
  const [busy, setBusy] = useState(false);
  const [pubKey, setPubKey] = useState("");
  const [exp, setExp] = useState(null);       // C3: importo atteso (noleggio + cauzione)
  const [accepted, setAccepted] = useState(false); // C3: il cliente ha accettato il prezzo finale

  const load = () => {
    axios.get(`${API}/partner/payments/${booking.reference}`, { headers: { Authorization: PTOK() } }).then(({ data }) => setPayment(data.payment)).catch(() => setPayment(null));
    axios.get(`${API}/partner/payments/expected/${booking.reference}`, { headers: { Authorization: PTOK() } }).then(({ data }) => { setExp(data); setAccepted(!!data.accepted); }).catch(() => setExp(null));
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const acceptPrice = async () => {
    if (!exp) return false;
    try {
      await axios.post(`${API}/payments/accept-price`, { booking_reference: booking.reference, amount: exp.amount });
      setAccepted(true);
      toast.success(`Prezzo finale €${exp.amount} accettato dal cliente (registrato con data e ora)`);
      return true;
    } catch (err) {
      toast.error(formatDetail(err.response?.data?.detail));
      return false;
    }
  };

  const authorize = async () => {
    if (!accepted && !(await acceptPrice())) return;
    setBusy(true);
    try {
      const cfg = await axios.get(`${API}/payments/config`).then((r) => r.data);
      setPubKey(cfg.publishable_key);
      const { data } = await axios.post(`${API}/partner/payments/intent`, { booking_reference: booking.reference }, { headers: { Authorization: PTOK() } });
      setStripeModal(data.client_secret);
      load();
    } catch (err) {
      toast.error(formatDetail(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const makeLink = async () => {
    if (!accepted && !(await acceptPrice())) return;
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/partner/payments/link`, { booking_reference: booking.reference, origin_url: window.location.origin, customer_phone: booking.customer?.phone || "" }, { headers: { Authorization: PTOK() } });
      setLink(data);
      load();
    } catch (err) {
      toast.error(formatDetail(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <div className="mt-2 rounded-xl bg-white border border-gray-200 p-3" data-testid={`partner-pay-${booking.reference}`}>
      <div className="flex items-center gap-2">
        <CreditCard size={14} className="text-fr-navy" />
        <p className="text-[11px] font-bold text-gray-600">Pagamento (noleggio + cauzione) — nessun dato carta salvato su FabriRent</p>
        {payment && <span className={`ml-auto text-[10px] font-bold rounded px-1.5 py-0.5 ${PSTAT_BADGE[payment.status] || "bg-gray-100"}`} data-testid={`partner-pay-status-${booking.reference}`}>{payment.status.toUpperCase()}</span>}
      </div>
      {payment === undefined ? null : payment ? (
        <p className="text-[10px] text-gray-400 mt-1">Importo €{payment.amount_total} (di cui cauzione €{payment.deposit_amount}). Il gestionale incasserà a fine noleggio o rilascerà la cauzione.</p>
      ) : (
        <div className="mt-2">
          {exp && (
            <label className="flex items-center gap-2 text-[11px] text-gray-600 mb-2" data-testid={`partner-pay-accept-${booking.reference}`}>
              <input type="checkbox" checked={accepted} onChange={(e) => (e.target.checked ? acceptPrice() : setAccepted(false))} className="accent-fr-navy" data-testid={`partner-pay-acceptbox-${booking.reference}`} />
              Il cliente accetta il prezzo finale: <b>€{exp.amount}</b> (noleggio €{(exp.amount - exp.deposit).toFixed(2)} + cauzione €{exp.deposit}) — registrato con data, ora e importo
            </label>
          )}
          <div className="flex flex-wrap gap-2">
            <button onClick={authorize} disabled={busy || (exp && !accepted)} className="h-9 rounded-lg bg-fr-navy text-white text-[11px] font-bold px-3 flex items-center gap-1 disabled:opacity-60" data-testid={`partner-pay-authorize-${booking.reference}`}>
              {busy ? <Loader2 size={12} className="animate-spin" /> : <Lock size={12} />} Autorizza con carta in struttura
            </button>
            <button onClick={makeLink} disabled={busy || (exp && !accepted)} className="h-9 rounded-lg bg-gray-100 text-gray-700 text-[11px] font-bold px-3 flex items-center gap-1 disabled:opacity-60" data-testid={`partner-pay-link-${booking.reference}`}>
              <ExternalLink size={12} /> Link per il cliente (WhatsApp/email)
            </button>
          </div>
        </div>
      )}
      {link?.url && (
        <div className="mt-2 rounded-lg bg-gray-50 border border-gray-100 p-2 text-[10px]" data-testid={`partner-pay-linkbox-${booking.reference}`}>
          <p className="break-all text-gray-500">{link.url}</p>
          <div className="mt-1 flex gap-2">
            <button onClick={() => { navigator.clipboard?.writeText(link.url); toast.success("Link copiato"); }} className="rounded-md bg-white border border-gray-200 px-2 py-1 font-bold" data-testid={`partner-pay-copy-${booking.reference}`}>Copia</button>
            {link.whatsapp_url && <a href={link.whatsapp_url} target="_blank" rel="noreferrer" className="rounded-md bg-green-600 text-white px-2 py-1 font-bold flex items-center gap-1" data-testid={`partner-pay-wa-${booking.reference}`}><MessageCircle size={11} /> WhatsApp</a>}
          </div>
        </div>
      )}
      {stripeModal && pubKey && (
        <StripeModal publishableKey={pubKey} clientSecret={stripeModal} booking={booking} onClose={() => { setStripeModal(null); load(); }} />
      )}
    </div>
  );
};
